import InfiniteScroll from 'react-infinite-scroll-component';
import { useMemo } from 'react';
import { IssuerTransactionItem } from '@/modules/issuer-dashboard/components/items/IssuerTransactionItem.tsx';
import { useTransactionsListQuery } from '@/modules/issuer-dashboard/queries/transactions.ts';
import { EmptyListState, Spinner } from 'ui';
import {
  GetTransactionsListV1SrTransactionsGetParams,
  MyActivitiesListV1SrActivitiesMyGetParams,
} from 'api';

export type TransactionFilters = Pick<
  MyActivitiesListV1SrActivitiesMyGetParams,
  | 'type'
  | 'status'
  | 'total_to'
  | 'register_class_id'
  | 'completion_date_from'
  | 'completion_date_to'
  | 'requested_at_date_from'
  | 'requested_at_date_to'
>;

export interface IssuerActivityTabProps {
  register_id: number;
  filters: TransactionFilters;
}

export const IssuerTransactionsTab = ({ register_id, filters }: IssuerActivityTabProps) => {
  const {
    data: issuerTransactionsList,
    fetchNextPage,
    hasNextPage,
    isLoading: isIssuerTransactionsLoading,
  } = useTransactionsListQuery(
    {
      page: 1,
      page_size: 15,
      confirmation_date_from: filters.completion_date_from,
      confirmation_date_to: filters.completion_date_to,
      ...(filters as unknown as GetTransactionsListV1SrTransactionsGetParams),
    },
    false,
    Number(register_id),
  );

  const appliedFiltersLength = useMemo(() => Object.keys(filters).length, [filters]);

  if (isIssuerTransactionsLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-start gap-4">
      <div className="w-full flex items-center justify-between">
        <span></span>
        <div></div>
      </div>
      <EmptyListState
        title={appliedFiltersLength > 0 ? 'Nothing found' : 'No activities yet'}
        description={
          appliedFiltersLength > 0
            ? 'No entries matching your filters'
            : 'To add transaction go to Activities tab and click on Add transaction, create and confirm this activity which will create completed transaction'
        }
        isLoading={isIssuerTransactionsLoading}
        isSearchUsed={false}
        itemsCount={issuerTransactionsList?.pages.flatMap(({ data }) => data).length ?? 0}
      />
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={issuerTransactionsList?.pages.length ?? 0}
        hasMore={hasNextPage || false}
        style={{ overflow: 'unset' }}
        loader={
          <div className="flex justify-center items-center h-16 w-full">
            <Spinner size="md" variant="primary" />
          </div>
        }
        scrollableTarget="scrollable"
      >
        <div className="w-full flex flex-col space-y-2">
          {issuerTransactionsList?.pages.map(({ data }) =>
            data.map(transaction => (
              <IssuerTransactionItem
                key={transaction.id}
                {...transaction}
                register_id={register_id}
                isDeleted={
                  (filters as unknown as GetTransactionsListV1SrTransactionsGetParams)?.is_deleted
                }
                showArchiveButton={true}
              />
            )),
          )}
        </div>
      </InfiniteScroll>
    </div>
  );
};
